import React, { useState, useEffect } from "react";
import { Briefcase, Bell, ChevronDown, User, ShieldCheck, Key, LogOut, CheckCircle2, Zap, X, Copy, Check, Menu, RefreshCw, Edit2, Save, Building2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Modal, message } from "antd";
import "../Styles/Header.scss";

const initialNotifications = [
  {
    id: 1,
    icon: Zap,
    color: "green",
    title: "InsightsAI API v2.4 Live",
    desc: "SQL query engine updated with 40% faster latency.",
    time: "10 mins ago",
    unread: true,
  },
  {
    id: 2,
    icon: CheckCircle2,
    color: "orange",
    title: "Commission Report Ready",
    desc: "Q3 Revenue Share of $14,250.00 ready for payout.",
    time: "2 hours ago",
    unread: true,
  },
  {
    id: 3,
    icon: ShieldCheck,
    color: "blue",
    title: "Security Key Rotated",
    desc: "DocsAI OAuth 2.0 SSL certificate renewed successfully.",
    time: "1 day ago",
    unread: true,
  },
];

const Header = ({ onOpenAssets }) => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [notifications, setNotifications] = useState(initialNotifications);
  const [copiedKey, setCopiedKey] = useState(false);

  // Dynamic Interactive Account Settings States
  const [userEmail, setUserEmail] = useState(() => sessionStorage.getItem("user_email") || localStorage.getItem("user_email") || "demo@partner.com");
  const [userName, setUserName] = useState(() => sessionStorage.getItem("user_name") || localStorage.getItem("user_name") || "Demo Partner");
  const [orgName, setOrgName] = useState(() => localStorage.getItem("partner_org_name") || "iNextLabs Enterprise");
  const [partnerTier, setPartnerTier] = useState(() => localStorage.getItem("partner_tier") || "Platinum Partner");
  const [apiKey, setApiKey] = useState(() => localStorage.getItem("partner_api_key") || "ink_live_99482810482019948");
  const [isEditingOrg, setIsEditingOrg] = useState(false);
  const [tempOrgName, setTempOrgName] = useState(orgName);

  const unreadCount = notifications.filter((n) => n.unread).length;

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".partner-nav-notification") && !e.target.closest(".partner-popover-notifications")) {
        setShowNotifications(false);
      }
      if (!e.target.closest(".partner-nav-user") && !e.target.closest(".partner-popover-user-menu")) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.removeItem("msal.account.keys");
    navigate("/login");
  };

  const handleAssetsClick = () => {
    if (onOpenAssets) {
      onOpenAssets();
    } else {
      const resourcesEl = document.getElementById("section-resources");
      if (resourcesEl) {
        resourcesEl.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const markAllRead = () => {
    setNotifications(notifications.map((n) => ({ ...n, unread: false })));
  };

  const toggleNotificationRead = (id) => {
    setNotifications(
      notifications.map((n) => (n.id === id ? { ...n, unread: !n.unread } : n))
    );
  };

  const handleCopyApiKey = () => {
    navigator.clipboard.writeText(apiKey);
    setCopiedKey(true);
    message.success("API Key copied to clipboard!");
    setTimeout(() => setCopiedKey(false), 2000);
  };

  const handleRegenerateApiKey = () => {
    const randomHex = Array.from({ length: 18 }, () => Math.floor(Math.random() * 16).toString(16)).join("");
    const newKey = `ink_live_${randomHex}`;
    setApiKey(newKey);
    localStorage.setItem("partner_api_key", newKey);
    message.success("New 256-bit API Access Key generated successfully!");
  };

  const handleSaveOrgName = () => {
    if (!tempOrgName.trim()) {
      message.error("Organization name cannot be empty.");
      return;
    }
    setOrgName(tempOrgName);
    localStorage.setItem("partner_org_name", tempOrgName);
    setIsEditingOrg(false);
    message.success("Organization name updated!");
  };

  const handleToggleTier = () => {
    const tiers = ["Platinum Partner", "Gold Partner", "Silver Partner"];
    const currentIndex = tiers.indexOf(partnerTier);
    const nextTier = tiers[(currentIndex + 1) % tiers.length];
    setPartnerTier(nextTier);
    localStorage.setItem("partner_tier", nextTier);
    message.info(`Partner status updated to ${nextTier}`);
  };

  return (
    <header className={`partner-navbar-header ${scrolled ? "scrolled" : ""}`}>
      <div className="partner-navbar-container">
        {/* Left Brand Logo & Status Tag */}
        <div className="partner-navbar-brand">
          <div className="partner-navbar-logo" onClick={() => navigate("/")}>
            <img
              src="/images/brand/inextlabs-logo.png"
              alt="iNextLabs Logo"
              className="partner-header-logo-img"
            />
          </div>
          <span className="partner-brand-divider" />
          <div className="partner-brand-tag-wrapper">
            <span className="partner-brand-status-dot" />
            <span className="partner-brand-tag">Partner Center</span>
          </div>
        </div>

        {/* Center Spatial Quick-Jump Navigation (Desktop >= 1024px) */}
        <div className="partner-header-center-nav">
          <button
            type="button"
            className="partner-center-nav-link"
            onClick={() => {
              const el = document.getElementById("section-overview");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
          >
            Overview
          </button>
          <button
            type="button"
            className="partner-center-nav-link"
            onClick={() => {
              const el = document.getElementById("section-demos");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
          >
            Demos
          </button>
          <button
            type="button"
            className="partner-center-nav-link"
            onClick={() => {
              const el = document.getElementById("section-pricing");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
          >
            Pricing
          </button>
          <button
            type="button"
            className="partner-center-nav-link"
            onClick={() => {
              const el = document.getElementById("section-resources");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
          >
            Resources
          </button>
        </div>

        {/* Right Spatial Nav Actions */}
        <div className="partner-navbar-actions">
          {/* Assets Button */}
          <button
            type="button"
            className="partner-nav-btn partner-nav-assets-pill"
            onClick={handleAssetsClick}
          >
            <Briefcase size={15} className="partner-nav-icon" />
            <span>Assets</span>
            <span className="partner-nav-sparkle-dot" />
          </button>

          {/* User Profile Dropdown Pill */}
          <div className="partner-popover-wrapper">
            <div
              className={`partner-nav-user ${showUserMenu ? "active" : ""}`}
              onClick={() => {
                setShowUserMenu(!showUserMenu);
              }}
            >
              <img
                src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80"
                alt={userName}
                className="partner-user-avatar"
              />
              <div className="partner-user-info">
                <span className="partner-user-name">
                  {userName}
                  <ChevronDown size={12} className={`partner-user-chevron ${showUserMenu ? "rotate" : ""}`} />
                </span>
                <span className="partner-user-role">Partner Admin</span>
              </div>
            </div>

            {showUserMenu && (
              <div className="partner-popover-user-menu apple-glass-popover">
                <div className="partner-user-menu-header">
                  <img
                    src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80"
                    alt={userName}
                    className="partner-user-menu-avatar"
                  />
                  <div className="partner-user-menu-details">
                    <span className="partner-user-menu-name">{userName}</span>
                    <span className="partner-user-menu-email">{userEmail}</span>
                    <span className="partner-user-menu-tier">
                      <ShieldCheck size={12} color="#10b981" />
                      Platinum Partner
                    </span>
                  </div>
                </div>

                <div className="partner-user-menu-divider" />

                <div className="partner-user-menu-items">
                  <button
                    type="button"
                    className="partner-menu-item"
                    onClick={() => {
                      setShowAccountModal(true);
                      setShowUserMenu(false);
                    }}
                  >
                    <User size={15} />
                    <span>Account Settings</span>
                  </button>
                  <button type="button" className="partner-menu-item danger" onClick={handleLogout}>
                    <LogOut size={15} />
                    <span>Log Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Apple Mobile Spatial Menu Trigger */}
          <button
            type="button"
            className="partner-mobile-menu-trigger"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle Navigation"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Apple Mobile Spatial Glass Full Sheet */}
      {mobileMenuOpen && (
        <div className="partner-apple-mobile-sheet">
          <div className="partner-mobile-sheet-content">
            <div className="partner-mobile-user-card">
              <img
                src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80"
                alt={userName}
              />
              <div>
                <h3>{userName}</h3>
                <span className="partner-tier-tag">
                  <ShieldCheck size={12} color="#10b981" /> Platinum Partner
                </span>
              </div>
            </div>

            <div className="partner-mobile-sheet-grid">
              <button
                type="button"
                className="mobile-sheet-btn"
                onClick={() => {
                  handleAssetsClick();
                  setMobileMenuOpen(false);
                }}
              >
                <Briefcase size={16} color="#f05742" />
                <span>Partner Assets</span>
              </button>
              <button
                type="button"
                className="mobile-sheet-btn"
                onClick={() => {
                  setShowAccountModal(true);
                  setMobileMenuOpen(false);
                }}
              >
                <User size={16} color="#3b82f6" />
                <span>Account Settings</span>
              </button>
            </div>

            <div className="partner-mobile-actions-stack">
              <button
                type="button"
                className="mobile-action-btn danger"
                onClick={handleLogout}
              >
                <LogOut size={16} />
                <span>Log Out</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Account Settings Apple Spatial Modal */}
      <Modal
        open={showAccountModal}
        onCancel={() => setShowAccountModal(false)}
        footer={null}
        width={560}
        closeIcon={
          <div className="apple-drawer-close-pill">
            <X size={16} />
          </div>
        }
        className="partner-apple-account-modal"
      >
        <div className="partner-modal-account-content">
          <div className="partner-modal-header">
            <div className="partner-modal-avatar-ring">
              <img
                src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80"
                alt={userName}
              />
            </div>
            <div className="partner-modal-user-meta">
              <h2>{userName}</h2>
              <span className="partner-modal-role-badge">Partner Admin</span>
              <p className="partner-modal-email">{userEmail}</p>
            </div>
          </div>

          <div className="partner-modal-grid">
            <div className="partner-modal-card" style={{ gridColumn: "1 / -1" }}>
              <div className="card-label-row">
                <span className="card-label">Organization</span>
                {!isEditingOrg ? (
                  <button type="button" className="card-edit-btn" onClick={() => { setIsEditingOrg(true); setTempOrgName(orgName); }}>
                    <Edit2 size={12} /> Edit
                  </button>
                ) : (
                  <button type="button" className="card-edit-btn save" onClick={handleSaveOrgName}>
                    <Save size={12} /> Save
                  </button>
                )}
              </div>

              {!isEditingOrg ? (
                <span className="card-value">{orgName}</span>
              ) : (
                <input
                  type="text"
                  className="card-value-input"
                  value={tempOrgName}
                  onChange={(e) => setTempOrgName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSaveOrgName()}
                  autoFocus
                />
              )}
            </div>
          </div>

          <div className="partner-modal-footer-actions">
            <button
              type="button"
              className="partner-modal-close-btn"
              onClick={() => setShowAccountModal(false)}
            >
              Close Settings
            </button>
          </div>
        </div>
      </Modal>
    </header>
  );
};

export default Header;
